// 수금(지급) 등록 다이얼로그 - 세금계산서 1건에 수금이 N번 나눠 들어올 수 있고(기존),
// 이제 계획(선금/중도금/잔금)도 N개라 "이 입금을 어느 계획에 배분할지"를 고를 수 있어야 한다.
// 사용자 승인(2026-10-21): 자동배분(FIFO) 기본값 + 수동 지정 + 계획 없이(미배정) 허용.
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

const fmt = (value) => {
  const n = Number(String(value ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n.toLocaleString('ko-KR') : '0';
};

const COLLECT_TYPES = [
  { value: 'CASH', label: '현금' },
  { value: 'NOTE', label: '어음' },
  { value: 'OFFSET', label: '상계' },
  { value: 'CARD', label: '카드' },
  { value: 'ETC', label: '기타' },
];

const MODE_AUTO = 'AUTO';
const MODE_MANUAL = 'MANUAL';
const MODE_UNLINKED = 'UNLINKED';

export default function BillCollectAddDialog({ open, onClose, row, onSubmit, submitting }) {
  const [collectDt, setCollectDt] = useState(dayjs());
  const [collectAmt, setCollectAmt] = useState('');
  const [collectTyp, setCollectTyp] = useState('CASH');
  const [mode, setMode] = useState(MODE_AUTO);
  const [planUuid, setPlanUuid] = useState('');
  const [remark, setRemark] = useState('');

  const plans = row?.plans || [];
  const unfinishedPlans = plans.filter((p) => Number(String(p.remainAmt || '0').replace(/,/g, '')) > 0);
  const remainAmt = Number(String(row?.collectedAmt || '0').replace(/,/g, '')) >= 0
    ? Number(String(row?.invTot || '0').replace(/,/g, '')) - Number(String(row?.collectedAmt || '0').replace(/,/g, ''))
    : 0;

  useEffect(() => {
    if (open) {
      setCollectDt(dayjs());
      setCollectAmt('');
      setCollectTyp('CASH');
      setMode(MODE_AUTO);
      setPlanUuid(unfinishedPlans[0]?.planUuid || '');
      setRemark('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, row?.invUuid]);

  const amtNum = Number(collectAmt) || 0;
  const overRemain = amtNum > 0 && amtNum > remainAmt;

  const handleSubmit = () => {
    const payload = {
      collectDt: collectDt ? collectDt.format('YYYY-MM-DD') : '',
      collectAmt: String(amtNum),
      collectTyp,
      remark: remark || null,
    };
    if (mode === MODE_UNLINKED) {
      payload.unlinked = true;
    } else if (mode === MODE_MANUAL) {
      payload.planUuid = planUuid || null;
    }
    onSubmit?.(payload);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>수금(지급) 등록 - {row?.invNo || ''}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          잔액 {fmt(remainAmt)}원
        </Typography>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="수금(지급)일자"
            format="YYYY-MM-DD"
            value={collectDt}
            onChange={setCollectDt}
            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
          />
        </LocalizationProvider>

        <TextField
          label="금액"
          type="number"
          size="small"
          fullWidth
          value={collectAmt}
          onChange={(e) => setCollectAmt(e.target.value)}
          error={overRemain}
          helperText={overRemain ? `잔액(${fmt(remainAmt)}원)을 초과할 수 없습니다` : ' '}
        />

        <FormControl size="small" fullWidth>
          <InputLabel>수금(지급)수단</InputLabel>
          <Select label="수금(지급)수단" value={collectTyp} onChange={(e) => setCollectTyp(e.target.value)}>
            {COLLECT_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>계획 배분</Typography>
          <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
            <FormControlLabel value={MODE_AUTO} control={<Radio size="small" />}
              label="자동배분(오래된 계획부터, 넘치면 다음 계획으로)" />
            <FormControlLabel value={MODE_MANUAL} control={<Radio size="small" />}
              label="계획 선택(그 계획부터 시작해 넘치면 이월)" />
            <FormControlLabel value={MODE_UNLINKED} control={<Radio size="small" />}
              label="계획 없이 등록(나중에 연결)" />
          </RadioGroup>
          {mode === MODE_MANUAL && (
            <FormControl size="small" fullWidth sx={{ mt: 1 }}>
              <InputLabel>계획</InputLabel>
              <Select label="계획" value={planUuid} onChange={(e) => setPlanUuid(e.target.value)}>
                {unfinishedPlans.map((p) => (
                  <MenuItem key={p.planUuid} value={p.planUuid}>
                    {p.planNm || '계획'} · {p.planDt} · 잔액 {fmt(p.remainAmt)}원
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {mode === MODE_MANUAL && unfinishedPlans.length === 0 && (
            <Alert severity="warning" sx={{ mt: 1 }}>미완료 계획이 없습니다. 계획 없이 등록하세요.</Alert>
          )}
        </Box>

        <TextField
          label="비고"
          size="small"
          fullWidth
          value={remark}
          onChange={(e) => setRemark(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>취소</Button>
        <Button
          variant="contained"
          disabled={submitting || amtNum <= 0 || overRemain || !collectDt}
          onClick={handleSubmit}
        >
          등록
        </Button>
      </DialogActions>
    </Dialog>
  );
}
